import { Component } from '@wix/thunderbolt-becky-types';
import {
  withCompInfo,
  createComponentMapperModel,
} from '@wix/editor-elements-integrations';
import {
  StylableHorizontalMenuDefinition,
  StylableHorizontalMenuProps,
  StylableHorizontalMenuCSSVars,
  StylableHorizontalMenuPropsCarmiData,
} from '../StylableHorizontalMenu.types';
import {
  parseValueFromCss,
  parseDirection,
  parseFlyoutAlignment,
} from './parseCss';

const isHorizontallyDocked = (layout: Component['layout']): boolean =>
  !!(layout.docked?.right && layout.docked?.left);

const getPositionBoxMargin = (
  margin: number,
  mainMenuStretched: boolean,
  subMenuStretched: boolean,
) => {
  const subMenuHasMargins = Boolean(margin);
  if (subMenuStretched) {
    // it submenu is stretched, it should be stretched to 100%, but may have own margins
    if (subMenuHasMargins) {
      return `calc(100% - ${2 * margin}px)`;
    }
    return '100%';
  }
  if (!mainMenuStretched && !subMenuStretched) {
    return '100%'; // if both menu & submenu are not stretched, submenu should fill menu width
  }

  return '980px'; // if submenu is not stretched, it should be contained inside page width
};

export const props = withCompInfo<
  StylableHorizontalMenuProps,
  StylableHorizontalMenuDefinition,
  StylableHorizontalMenuPropsCarmiData
>()(
  ['compProps', 'compLayout', 'compStylableClass', 'styleProperties'],
  (
    { compProps, compLayout, compStylableClass, styleProperties },
    carmiProps,
  ) => {
    const { menuMode, submenuMode, submenuOrder } = compProps;
    const cssText = styleProperties['st-css'];

    return {
      menuMode,
      submenuMode,
      submenuOrder,
      items: carmiProps.items,
      isStretched: isHorizontallyDocked(compLayout),
      className: compStylableClass,
      direction: parseDirection(cssText),
      flyoutAlignment: parseFlyoutAlignment(cssText),
      isHeightAuto: menuMode === 'wrap',
    };
  },
);

export const css = withCompInfo<
  StylableHorizontalMenuCSSVars,
  StylableHorizontalMenuDefinition
>()(
  ['compProps', 'styleProperties', 'compLayout'],
  ({
    compProps: { menuMode, submenuMode, submenuOrder },
    styleProperties,
    compLayout,
  }) => {
    const cssText = styleProperties['st-css'];
    const isRTL = parseDirection(cssText) === 'rtl';
    const isScrollable = menuMode === 'scroll';
    const isFlyout = submenuMode === 'flyout';
    const isMainMenuStretched = isHorizontallyDocked(compLayout);
    const isSubmenuStretched = submenuMode === 'columnStretched';
    const subMenuMarginLeft = parseValueFromCss(
      cssText,
      'positionBox',
      'margin-left',
      0,
    );
    const subMenuMarginBottom = parseValueFromCss(
      cssText,
      'columnsLayout::menuItem',
      'margin-bottom',
      0,
    );
    return {
      '--white-space': isFlyout ? 'nowrap' : 'normal',
      '--label-word-wrap': isFlyout ? 'normal' : 'break-word',
      '--menu-flex-wrap': isScrollable ? 'nowrap' : 'wrap',
      '--submenu-box-position': isFlyout ? 'fixed' : 'absolute',
      '--submenu-box-margin-top':
        isFlyout || isScrollable
          ? 0
          : -1 * parseValueFromCss(cssText, 'menuItem', 'margin-bottom', 0),
      '--columns-display':
        !isFlyout && submenuOrder === 'horizontal' ? 'grid' : 'block',
      '--submenu-width': isFlyout
        ? 100
        : parseValueFromCss(
            cssText,
            'columnsLayout::listWrapper',
            'width',
            100,
          ),
      '--submenu-margin-bottom': subMenuMarginBottom,
      '--subsubmenu-margin-bottom': isFlyout ? subMenuMarginBottom : 0,
      '--subsubmenu-box-opacity': isFlyout ? 0 : 1,
      '--subsubmenu-box-pointer-events': isFlyout ? 'none' : 'inherit',
      '--subsubmenu-box-position': isFlyout ? 'absolute' : 'static',
      '--subsubmenu-box-show-animation': isFlyout ? 1 : 0,
      '--subsubmenu-box-left': isRTL ? 'unset' : '100%',
      '--subsubmenu-box-right': isRTL ? '100%' : 'unset',
      '--subsubmenu-box-translate-x-coef': isRTL ? 1 : -1,
      ...(!isFlyout && {
        '--submenu-box-width': getPositionBoxMargin(
          subMenuMarginLeft,
          isMainMenuStretched,
          isSubmenuStretched,
        ),
        '--submenu-box-left': 0,
        '--submenu-box-right': 0,
        '--submenu-box-margin': isSubmenuStretched
          ? `${subMenuMarginLeft}px`
          : 'auto',
        '--columns-count': parseValueFromCss(
          cssText,
          'columnsLayout::listWrapper',
          '--columnsAmount',
          3,
        ),
        '--submenu-justify-content': parseValueFromCss(
          cssText,
          'columnsLayout::pageWrapper',
          'justify-content',
          'flex-start',
        ),
        '--horizontal-spacing': parseValueFromCss(
          cssText,
          'columnsLayout',
          '--horizontalSpacing',
          0,
        ),
        [submenuOrder === 'horizontal'
          ? '--vertical-spacing'
          : '--category-spacing']: -parseValueFromCss(
          cssText,
          'columnsLayout::flex',
          'margin-bottom',
          0,
        ),
      }),
    } as StylableHorizontalMenuCSSVars;
  },
);

export default createComponentMapperModel({ css, props });
